/**
 * Click-to-select on the availability calendar: first click is the arrival,
 * second click the departure; the same day twice is a day rental. Pure, so the
 * click rules and the validation are testable without a browser.
 */

import {
  plainDate,
  priceQuote,
  STANDAARD_TARIEVEN,
  type Verhuurtype,
  verhuurtypeVoor,
} from "@bivak/domain";

export type Selectie = {
  van?: string | undefined;
  tot?: string | undefined;
};

/**
 * The next selection after clicking `dagIso`.
 *
 * No arrival yet → this is the arrival. Arrival set → this is the departure
 * (the same day means a day rental; an earlier day restarts). Both set → a new
 * selection starts.
 */
export function volgendeSelectie(huidige: Selectie, dagIso: string): Selectie {
  if (huidige.van === undefined || huidige.tot !== undefined) {
    return { van: dagIso, tot: undefined };
  }
  if (dagIso < huidige.van) return { van: dagIso, tot: undefined };
  return { van: huidige.van, tot: dagIso };
}

export type SelectieResultaat =
  | {
      readonly ok: true;
      readonly verhuurtype: Verhuurtype;
      readonly van: string;
      readonly tot: string;
    }
  | {
      readonly ok: false;
      readonly reason: "bezet" | "seizoensgrens" | "geen_kamp_in_schooljaar" | "ongeldig";
    };

type Range = { from: string; to: string };

/**
 * Validate a completed selection against the space's unavailable ranges and
 * the seasons rule. `[van, tot)` half-open, like the occupancy itself: the
 * departure day may be someone else's arrival, and a day rental occupies the
 * whole day `[van, van + 1)`.
 */
export function selectieResultaat(
  van: string,
  tot: string,
  unavailable: readonly Range[],
): SelectieResultaat {
  const arrival = plainDate(van);
  const departure = plainDate(tot);

  const occupiedUntil = van === tot ? arrival.add({ days: 1 }).toString() : tot;
  const taken = unavailable.some((held) => held.from < occupiedUntil && van < held.to);
  if (taken) return { ok: false, reason: "bezet" };

  const typed = verhuurtypeVoor(arrival, departure);
  if (!typed.ok) return { ok: false, reason: typed.reason };

  // Belt and braces: with the standard scheme this dry-run cannot fail beyond
  // the season reasons already returned above — it becomes a live check once
  // tariffs are loaded from the database instead of the constant.
  const quote = priceQuote(
    [
      {
        spaceId: "selectie",
        verhuurtype: typed.verhuurtype,
        validFrom: null,
        validTo: null,
        ...STANDAARD_TARIEVEN[typed.verhuurtype],
      },
    ],
    { spaceId: "selectie", arrival, departure, persons: 1 },
  );
  if (!quote.ok) return { ok: false, reason: "ongeldig" };

  return { ok: true, verhuurtype: typed.verhuurtype, van, tot };
}
